/**
 * An answer as a reviewer reads it, not as a JSON tree: each field a label
 * over its value — text as prose with its own line breaks (never cut short),
 * numbers and true / false in mono, a short list as chips, anything nested as
 * an indented list of its own fields. A Fields / JSON switch keeps the raw
 * tree one click away.
 */

import { useState } from "react";
import { JsonView } from "@/components/ui/json-view";
import { Segmented } from "@/components/ui/tabs";

const MAX_DEPTH = 6;

const isPlainList = (v: unknown[]) => v.every((x) => x === null || typeof x !== "object");

function Scalar({ value }: { value: unknown }) {
  if (value === null || value === undefined)
    return <span className="font-mono text-xs text-dim italic">null</span>;
  if (typeof value === "string") {
    return value === "" ? (
      <span className="font-mono text-xs text-dim">""</span>
    ) : (
      <p className="m-0 text-[13px] leading-relaxed break-words whitespace-pre-wrap text-code">{value}</p>
    );
  }
  return <span className="font-mono text-[13px] text-ink">{String(value)}</span>;
}

function Fields({ value, depth }: { value: unknown; depth: number }) {
  if (value === null || typeof value !== "object") return <Scalar value={value} />;
  if (depth >= MAX_DEPTH) return <JsonView value={value} label="Nested value" />;
  if (Array.isArray(value)) {
    if (!value.length) return <span className="font-mono text-xs text-dim">empty list</span>;
    if (isPlainList(value)) {
      return (
        <span className="flex flex-wrap gap-1">
          {value.map((v, i) => (
            <span key={i} className="field-chip">
              {v === null ? "null" : String(v)}
            </span>
          ))}
        </span>
      );
    }
    return (
      <ol className="m-0 flex list-none flex-col gap-2 border-l border-line-soft p-0 pl-3">
        {value.map((v, i) => (
          <li key={i} className="flex flex-col gap-0.5">
            <span className="font-mono text-[11px] text-faint">{i + 1}</span>
            <Fields value={v} depth={depth + 1} />
          </li>
        ))}
      </ol>
    );
  }
  const entries = Object.entries(value);
  if (!entries.length) return <span className="font-mono text-xs text-dim">no fields</span>;
  return (
    <dl className={`m-0 flex flex-col gap-2.5 ${depth ? "border-l border-line-soft pl-3" : ""}`}>
      {entries.map(([k, v]) => (
        <div key={k} className="flex min-w-0 flex-col gap-0.5">
          <dt className="font-mono text-xs text-muted">{k}</dt>
          <dd className="m-0 min-w-0">
            <Fields value={v} depth={depth + 1} />
          </dd>
        </div>
      ))}
    </dl>
  );
}

export function AnswerView({ value, label }: { value: unknown; label: string }) {
  const structured = value !== null && typeof value === "object";
  const [mode, setMode] = useState<"fields" | "json">("fields");
  if (!structured) {
    return (
      <div aria-label={label} className="rounded-lg border border-line-soft bg-well px-3.5 py-2.5">
        <Scalar value={value} />
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      <Segmented
        label={`${label} view`}
        value={mode}
        onChange={setMode}
        items={[
          { key: "fields", label: "Fields" },
          { key: "json", label: "JSON" },
        ]}
      />
      {mode === "fields" ? (
        <div aria-label={label} className="rounded-lg border border-line-soft bg-well px-3.5 py-3">
          <Fields value={value} depth={0} />
        </div>
      ) : (
        <JsonView value={value} label={label} />
      )}
    </div>
  );
}
